import { FC, createContext, useState, useContext, useEffect } from 'react';
import { publicIpv4 } from 'public-ip';
import {
  SorensonAccountProvider,
  LogInCredentials,
  Core,
  UserLoginStatus,
  InterfaceMode,
} from '@sorenson-eng/videophone-core-services';

import {
  setLocalItem,
  getLocalValue,
  removeLocalItem,
} from 'utils/services/storage';
import { useAuth0 } from '@auth0/auth0-react';
import UserType from '../constants/userType';
import { LoginModes } from 'utils/constants/loginModes';
import { LOCATION } from 'utils/constants';
import { ERROR_STRINGS } from 'utils/constants/strings';
import * as Sentry from '@sentry/react';
import axios from 'axios';

interface ISorensonUser {
  name: string;
  phoneNumber?: number;
  email?: string;
  userType: UserType;
  billingId?: string;
  billingName?: string;
}

interface IAuthContext {
  sorensonUser: ISorensonUser;
  isLoggedIn: boolean;
  ip: string;
  location: string | null;
  setUserData: (
    name: string,
    phoneNumber: number,
    userType: UserType,
    email?: string
  ) => void;
  clearUserData: () => void;
  signIn: (data: LogInCredentials) => void;
  signOut: () => void;
  auth0Login: () => void;
  setLocation: (state: string) => void;
  getTokenSilently: () => Promise<string>;
  loading: boolean;
  userInterfaceModeError: string;
}

declare let pendo: any;

const sorensonUserInitial: ISorensonUser = {
  name: '',
  phoneNumber: 0,
  userType: UserType.BLANK,
};

export const AuthContext = createContext<IAuthContext>({
  sorensonUser: sorensonUserInitial,
  isLoggedIn: false,
  ip: '',
  location: '',
  setUserData: (userName: string, userPhoneNumber: number) => {
    console.log(userName, userPhoneNumber);
  },
  clearUserData: () => {},
  signIn: () => {},
  signOut: () => {},
  auth0Login: () => {},
  setLocation: () => {},
  getTokenSilently: async () => '',
  loading: false,
  userInterfaceModeError: '',
});

export const AuthContextProvider: FC = ({ children }) => {
  const [mode, setMode] = useState<LoginModes>();
  const [sorensonUser, setSorensonUser] = useState<ISorensonUser>(
    getLocalValue<ISorensonUser>('sorenson_user') || sorensonUserInitial
  );
  if (process.env.REACT_APP_CORE_REQUEST_ENDPOINT)
    Core.updateConfig({
      coreRequestUrl: process.env.REACT_APP_CORE_REQUEST_ENDPOINT,
    });
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(
    (getLocalValue<boolean>('is_logged_in') as boolean) || false
  );
  const [loading, setLoading] = useState(true);
  const [ip, setIp] = useState<string>('');
  const [location, setLocation] = useState<string | null>(
    getLocalValue<string>(LOCATION)
  );
  const [userInterfaceModeError, setUserInterfaceModeError] =
    useState<string>('');

  const {
    loginWithRedirect,
    user: auth0User,
    isAuthenticated,
    isLoading,
    error,
    getAccessTokenSilently,
    logout,
  } = useAuth0();

  const setUserData = (
    name: string,
    phoneNumber: number,
    userType: UserType,
    email?: string
  ) => {
    const userData = { name, phoneNumber, userType, email };
    setSorensonUser(userData);
    setLocalItem('sorenson_user', JSON.stringify(userData));
  };

  const clearUserData = () => {
    setSorensonUser(sorensonUserInitial);
    setLocation('');
    removeLocalItem(LOCATION);
    removeLocalItem('sorenson_user');
    removeLocalItem('is_logged_in');
  };

  const signIn = async ({ username, password }: LogInCredentials) => {
    setMode(LoginModes.Sorenson);
    const provider = new SorensonAccountProvider();
    Core.updateConfig({
      coreRequestUrl: process.env.REACT_APP_CORE_REQUEST_URL,
    });
    try {
      const { loginStatus, statusMessage, interfaceMode } =
        await provider.userLoginCheck({
          username,
          password,
        });
      if (
        [
          InterfaceMode.PortedMode,
          InterfaceMode.HearingMode,
          InterfaceMode.EntityHearingAssigned,
        ].includes(interfaceMode)
      ) {
        setUserInterfaceModeError(ERROR_STRINGS.UNAUTHORIZED_PHONE);
        throw new Error(ERROR_STRINGS.UNAUTHORIZED_PHONE);
      }
      if (loginStatus !== UserLoginStatus.notValid) {
        setUserData(username, +username, UserType.VRS);
        setIsLoggedIn(true);
        setLocalItem<boolean>('is_logged_in', true);
        handlePendo(username);
      } else {
        setUserInterfaceModeError(`${ERROR_STRINGS.SIGN_IN_FAILURE} Or `);
        throw new Error(statusMessage);
      }
    } catch (e) {
      console.log('There was an error while logging in', e);
      Sentry.captureException(e);
      throw e;
    }
  };

  const signOut = () => {
    if (mode === LoginModes.Auth0) {
      clearUserData();
      setIsLoggedIn(false);
      logout({ returnTo: `${window.location.origin}/proxy/zoomapp/login` });
    } else {
      clearUserData();
      setIsLoggedIn(false);
      logout({ returnTo: `${window.location.origin}/proxy/zoomapp/login` });
    }
  };

  const auth0Login = () => {
    loginWithRedirect();
  };

  const getTokenSilently = async () => {
    if (mode === LoginModes.Auth0) {
      return await getAccessTokenSilently();
    }
    return '';
  };

  const getIp = async () => {
    try {
      const userPublicIp = await publicIpv4();
      setIp(userPublicIp);
    } catch (error) {
      console.log(error);
    }
  };

  const handlePendo = (
    id: string,
    billingId?: string,
    billingName?: string
  ) => {
    if (!pendo) {
      return;
    }
    pendo.identify({
      visitor: {
        id,
      },
      account: {
        id: billingName,
        billing_id: billingId,
      },
    });
  };

  useEffect(() => {
    if (!ip) {
      getIp();
    }
  }, [ip]);
  useEffect(() => {
    const locationValue = getLocalValue<string>(LOCATION);
    if (locationValue) setLocation(locationValue);
    const user = getLocalValue<ISorensonUser>('sorenson_user');
    setMode(user?.userType === 'VRI' ? LoginModes.Auth0 : LoginModes.Sorenson);
  }, []);

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      setMode(LoginModes.Auth0);
      setIsLoggedIn(true);
    }
  }, [error, isAuthenticated, isLoading]);

  useEffect(() => {
    const getBillingData = async (email: string) => {
      try {
        const token = await getAccessTokenSilently();
        const { data } = await axios.post(
          `${process.env.REACT_APP_BILLING_API_BASE_URL}/GetUserBillingInformation`,
          { contact_email: email },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        return { billingId: data.BillingId, billingName: data.BillingName };
      } catch (error) {
        console.error('Error: ', error);
      }
    };

    const setUserInfoWithAuth0 = async () => {
      if (auth0User?.name && auth0User?.email) {
        const { email, name } = auth0User;
        const billingData = await getBillingData(email);

        if (!billingData) return;
        const { billingId, billingName } = billingData;

        handlePendo(email, billingId, billingName);

        setSorensonUser({
          name,
          email,
          userType: UserType.VRI,
          billingId,
          billingName,
        });
      }
    };

    setUserInfoWithAuth0();
  }, [auth0User, getAccessTokenSilently]);

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  return (
    <AuthContext.Provider
      value={{
        sorensonUser,
        isLoggedIn,
        ip,
        location,
        loading,
        userInterfaceModeError,
        setUserData,
        clearUserData,
        signIn,
        signOut,
        auth0Login,
        setLocation,
        getTokenSilently,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
